import React, { useState } from 'react';
import { X, Check, Calendar, Zap, Mail, Sparkles, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

const PaymentSuccessModal = ({ onClose }) => {
  const { currentUser, currentOrg, subscription } = useAuth();
  const t = useTranslation();
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get plan information from subscription
  const getPlanInfo = () => {
    if (!subscription || !subscription.productName) {
      return {
        planName: 'Professional',
        features: ['Up to 10,000 searches per month', 'Batch screening', 'REST API Access']
      };
    }

    const productName = subscription.productName.toLowerCase();
    
    if (productName.includes('starter')) {
      return {
        planName: 'Starter',
        features: ['Up to 1,000 searches per month', '3 team members', 'Active Monitoring']
      };
    } else if (productName.includes('professional')) {
      return {
        planName: 'Professional', 
        features: ['Up to 10,000 searches per month', '10 team members', 'Batch screening', 'REST API Access']
      };
    } else if (productName.includes('enterprise')) {
      return {
        planName: 'Enterprise',
        features: ['Unlimited searches', 'Dedicated support', 'Custom integrations']
      };
    }

    return {
      planName: subscription.productName,
      features: ['Premium features included']
    };
  };

  // Format renewal date
  const getRenewalDate = () => {
    if (!subscription || !subscription.currentPeriodEnd) {
      return 'Next month';
    }

    try {
      let date;
      if (subscription.currentPeriodEnd.toDate) {
        // Firestore Timestamp
        date = subscription.currentPeriodEnd.toDate();
      } else if (subscription.currentPeriodEnd.seconds) {
        // Firestore Timestamp object
        date = new Date(subscription.currentPeriodEnd.seconds * 1000);
      } else {
        // Regular date
        date = new Date(subscription.currentPeriodEnd);
      }
      
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (error) {
      console.error('Error formatting renewal date:', error);
      return 'Next billing cycle';
    }
  };

  const handleContinue = async () => {
    setIsLoading(true);
    
    try {
      // Update user document with marketing preference
      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          marketing: marketingOptIn,
          marketingOptInDate: new Date().toISOString(),
          updatedAt: new Date()
        });
        console.log('Marketing preference saved:', marketingOptIn);
      }
      
      // Close modal and redirect
      onClose();
    } catch (error) {
      console.error('Error saving marketing preference:', error);
      // Continue anyway - don't block the user for this
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const planInfo = getPlanInfo();
  const renewalDate = getRenewalDate();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-[60] pt-24 pb-4 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-600 max-w-2xl w-full overflow-hidden relative ring-1 ring-black ring-opacity-5">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-8 text-white relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-emerald-600/20"></div>
          <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-white/5 rounded-full"></div>
          
          {/* Content */}
          <div className="relative text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-2">
              {t('payment.success.title', 'Thank You for Subscribing!')}
            </h2>
            <p className="text-green-100 text-lg">
              {t('payment.success.subtitle', 'Welcome to enhanced sanctions screening')}
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {/* Subscription Details */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
                  {planInfo.planName} Plan
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('payment.success.planActive', 'Your subscription is now active')}
                </p>
              </div>
              <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-3 py-1 rounded-full text-sm font-medium">
                {t('common.active', 'Active')}
              </div>
            </div>

            {/* Plan Features */}
            <div className="mb-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                {t('payment.success.features', 'Your plan includes:')}
              </h4>
              <ul className="space-y-2">
                {planInfo.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                    <ShieldCheck className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Renewal Info */}
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 pt-4 border-t border-gray-200 dark:border-gray-600">
              <Calendar className="w-4 h-4 mr-2" />
              <span>
                {t('payment.success.nextRenewal', 'Next renewal')}: <strong>{renewalDate}</strong>
              </span>
            </div>
          </div>

          {/* Marketing Opt-in */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 pt-1">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="flex-grow">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {t('payment.success.stayUpdated', 'Stay Ahead of Sanctions Changes')}
                </h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
                  {t('payment.success.marketingText', 'Get priority alerts on critical sanctions updates, regulatory changes, and new compliance features that could impact your business.')}
                </p>
                
                <label className="flex items-start space-x-3 cursor-pointer">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={marketingOptIn}
                      onChange={(e) => setMarketingOptIn(e.target.checked)}
                      className="w-5 h-5 text-blue-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    {marketingOptIn && (
                      <Check className="w-3 h-3 text-white absolute left-1 top-1 pointer-events-none" />
                    )}
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('payment.success.marketingOptIn', 'Yes, keep me informed about important sanctions and compliance updates')}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <div className="flex justify-center">
            <button
              onClick={handleContinue}
              disabled={isLoading}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>{t('common.saving', 'Saving...')}</span>
                </>
              ) : (
                <>
                  <span>{t('payment.success.continue', 'Continue to Dashboard')}</span>
                  <Zap className="w-5 h-5" />
                </>
              )}
            </button>
          </div>

          {/* Footer note */}
          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-4">
            {t('payment.success.footerNote', 'You can manage your subscription and preferences anytime in your account settings.')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessModal; 